import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { OWNER_STATUS, CONTACT_PREFS, PROPERTY_STATUS, DOC_STATUS, fmtDate, canCreate, isManager } from "../lib/re";
import { Badge, Spinner, Empty, Pagination, Btn, Modal, Tabs, Timeline } from "../components/re/ui";
import { OwnerForm } from "../components/re/OwnerForm";
import { toast } from "sonner";
import { Plus, Search, Building2, Phone, Mail, Pencil, Trash2, Send } from "lucide-react";

function ContactLog({ contacts, onAdd, editable }) {
  const [note, setNote] = useState("");
  const [type, setType] = useState("chamada");
  const add = async () => { if (!note.trim()) return; await onAdd({ type, note }); setNote(""); };
  return (
    <div className="space-y-4">
      {editable && (
        <div className="flex flex-col sm:flex-row gap-2">
          <select className="input-almar sm:w-36" value={type} onChange={(e) => setType(e.target.value)} data-testid="contact-type-select">
            <option value="chamada">Chamada</option><option value="email">Email</option><option value="reuniao">Reunião</option><option value="whatsapp">WhatsApp</option><option value="nota">Nota</option>
          </select>
          <input className="input-almar flex-1" placeholder="Registar contacto ou nota" value={note} onChange={(e) => setNote(e.target.value)} data-testid="contact-note-input" />
          <Btn onClick={add} data-testid="contact-add-btn"><Send size={14} /> Registar</Btn>
        </div>
      )}
      <Timeline items={(contacts || []).slice().reverse()} testId="contact-timeline" />
    </div>
  );
}

export function ContactLogExport(props) { return <ContactLog {...props} />; }

function OwnerDetail({ id, onClose, onEdit, onDeleted }) {
  const { user } = useAuth();
  const [o, setO] = useState(null);
  const [tab, setTab] = useState("imoveis");
  const load = useCallback(() => api.get(`/re/owners/${id}`).then((r) => setO(r.data)).catch(() => { toast.error("Proprietário não encontrado"); onClose(); }), [id, onClose]);
  useEffect(() => { load(); }, [load]);
  if (!o) return <Modal title="Proprietário" onClose={onClose} wide><Spinner /></Modal>;
  const addContact = async (c) => { try { await api.post(`/re/owners/${id}/contacts`, c); toast.success("Contacto registado"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); } };
  const del = async () => {
    if (!window.confirm("Eliminar definitivamente este proprietário e os seus dados pessoais?")) return;
    try { await api.delete(`/re/owners/${id}`); toast.success("Proprietário eliminado"); onDeleted(); } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };
  return (
    <Modal title={o.name} onClose={onClose} wide testId="owner-detail-modal">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-sm">
          <p className="text-slate-400">Tipo: <span className="text-slate-200">{o.owner_type === "empresa" ? "Empresa" : "Particular"}</span></p>
          {o.nif !== undefined && <p className="text-slate-400">NIF: <span className="text-slate-200" data-testid="owner-detail-nif">{o.nif || "—"}</span></p>}
          <p className="text-slate-400">Telefone: <span className="text-slate-200">{o.phone || "—"}</span></p>
          <p className="text-slate-400">Email: <span className="text-slate-200">{o.email || "—"}</span></p>
          <p className="text-slate-400">Preferência: <span className="text-slate-200">{CONTACT_PREFS[o.contact_preference] || "—"}</span></p>
          <p className="text-slate-400">Consultor: <span className="text-slate-200">{o.consultant_name || "—"}</span></p>
          <p className="text-slate-400">Registo: <span className="text-slate-200">{fmtDate(o.created_at)}</span></p>
          <p className="text-slate-400">Consentimento: <span className="text-slate-200">{o.consent_marketing ? `Sim (${fmtDate(o.consent_date)})` : "Não"}</span></p>
          {o.internal_notes && <p className="text-slate-400 sm:col-span-2">Notas: <span className="text-slate-200">{o.internal_notes}</span></p>}
        </div>
        <div className="flex gap-2">
          <Badge value={o.relation_status === "ativo" ? "disponivel" : o.relation_status === "inativo" ? "inativo" : "reservado"} label={OWNER_STATUS[o.relation_status]} />
          {o.can_edit && <Btn variant="ghost" onClick={() => onEdit(o)} data-testid="owner-edit-btn"><Pencil size={14} /></Btn>}
          {isManager(user) && <Btn variant="danger" onClick={del} data-testid="owner-delete-btn"><Trash2 size={14} /></Btn>}
        </div>
      </div>
      <Tabs active={tab} onChange={setTab} tabs={[
        { id: "imoveis", label: "Imóveis", count: o.properties.length },
        ...(o.documents ? [{ id: "documentos", label: "Documentos", count: o.documents.length }] : []),
        ...(o.contacts !== undefined ? [{ id: "contactos", label: "Contactos" }] : []),
        { id: "historico", label: "Histórico" },
      ]} />
      {tab === "imoveis" && (o.properties.length ? o.properties.map((p) => (
        <Link key={p.id} to={`/imoveis/${p.id}`} className="card-almar p-3 mb-2 flex items-center justify-between gap-3">
          <span className="text-sm text-slate-100 truncate"><span className="font-mono text-xs text-slate-400 mr-2">{p.reference}</span>{p.title}</span>
          <Badge value={p.status} label={PROPERTY_STATUS[p.status]} />
        </Link>)) : <p className="text-sm text-slate-500">Sem imóveis associados.</p>)}
      {tab === "documentos" && (o.documents.length ? o.documents.map((d) => (
        <div key={d.id} className="card-almar p-3 mb-2 flex items-center justify-between gap-3">
          <span className="text-sm text-slate-200 truncate">{d.name} <span className="text-xs text-slate-500">· {d.property_ref}</span></span>
          <Badge value={d.status} label={DOC_STATUS[d.status]} />
        </div>)) : <p className="text-sm text-slate-500">Sem documentos.</p>)}
      {tab === "contactos" && <ContactLog contacts={o.contacts} onAdd={addContact} editable={o.can_edit} />}
      {tab === "historico" && <Timeline items={o.history} />}
      <p className="text-[11px] text-slate-500 mt-4">Visitas, propostas e contratos associados ficarão disponíveis com os módulos de Visitas, Propostas e Negócios.</p>
    </Modal>
  );
}

export default function Proprietarios() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const detailId = params.get("id");

  const load = useCallback(() => {
    api.get("/re/owners", { params: { q: q || undefined, page } }).then((r) => setData(r.data))
      .catch(() => { toast.error("Erro ao carregar proprietários"); setData({ items: [], total: 0, pages: 1, page: 1 }); });
  }, [q, page]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openDetail = (id) => setParams(id ? { id } : {});
  const closeDetail = useCallback(() => setParams({}), [setParams]);

  return (
    <Layout title="Proprietários">
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input className="input-almar pl-9" placeholder="Pesquisar por nome, referência de imóvel, contacto ou NIF" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="owner-search-input" />
        </div>
        {canCreate(user) && <Btn onClick={() => setForm({})} data-testid="new-owner-btn"><Plus size={16} /> Novo proprietário</Btn>}
      </div>
      {!data ? <Spinner /> : data.items.length === 0 ? <Empty text="Nenhum proprietário encontrado." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="owners-grid">
          {data.items.map((o) => (
            <button key={o.id} onClick={() => openDetail(o.id)} data-testid={`owner-card-${o.id}`} className="card-almar p-5 text-left">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0"><p className="text-slate-100 font-semibold truncate">{o.name}</p><p className="text-xs text-slate-500">{o.owner_type === "empresa" ? "Empresa" : "Particular"} · {o.consultant_name}</p></div>
                <span className="text-xs text-[#E0C574] flex items-center gap-1"><Building2 size={13} />{o.property_count}</span>
              </div>
              <div className="mt-3 space-y-1 text-xs text-slate-400">
                <p className="flex items-center gap-2"><Phone size={12} />{o.phone || "—"}</p>
                <p className="flex items-center gap-2 truncate"><Mail size={12} />{o.email || "—"}</p>
              </div>
            </button>
          ))}
        </div>
      )}
      {data && data.items.length > 0 && <Pagination page={data.page} pages={data.pages} total={data.total} onChange={setPage} />}
      {form && <OwnerForm owner={form.id ? form : null} onClose={() => setForm(null)} onSaved={(o) => { setForm(null); load(); openDetail(o.id); }} />}
      {detailId && !form && <OwnerDetail key={detailId} id={detailId} onClose={closeDetail} onEdit={(o) => setForm(o)} onDeleted={() => { closeDetail(); load(); }} />}
    </Layout>
  );
}
